// src/script/crm/validation.js

const ALPHA_REGEX  = /^[a-zA-ZÀ-ÿ\u0600-\u06FF\s'-]+$/;
const EMAIL_REGEX  = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// ✅ Bonne regex pour 8 chiffres commençant par 2,4,5,7,9
const TEL_TN_REGEX = /^[24579]\d{7}$/;

// Fonction de nettoyage
export const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  let cleaned = String(phone).replace(/[^\d]/g, '');
  
  if (cleaned.startsWith('216') && cleaned.length === 11) cleaned = cleaned.slice(3);
  else if (cleaned.startsWith('00216') && cleaned.length === 13) cleaned = cleaned.slice(5);
  else if (cleaned.startsWith('0') && cleaned.length === 9) cleaned = cleaned.slice(1);
  
  return cleaned;
};

const VALID_PAYS   = ['Tunisie', 'France', 'Algérie', 'Maroc', 'Belgique', 'Canada', 'Autre'];
const VALID_SOURCE = [
  'Facebook', 'Instagram', 'TikTok', 'LinkedIn',
  'Google', 'Site web', 'Recommandation', 'Appel entrant', 'Autre',
];
const VALID_NIV    = ['Débutant', 'Intermédiaire', 'Avancé'];
const VALID_MODE   = ['Présentiel', 'En ligne', 'Hybride'];
const VALID_STATUT = ['Très intéressé', 'Intéressé moyen', 'Pas intéressé', 'Perdu'];

const VALID_GENRE         = ['Homme', 'Femme', 'Autre'];
// Ordre et libellés demandés par l'équipe ; « Secondaire (année à préciser) »
// n'est plus proposé à la saisie mais reste valide pour les fiches existantes.
export const NIVEAUX_ANNEE_SECONDAIRE = [
  '1ère année secondaire', '2ème année secondaire', '3ème année secondaire',
  '4ème année secondaire', '5ème année secondaire', '6ème année secondaire',
];
export const NIVEAU_SECONDAIRE_ANCIEN = 'Secondaire (année à préciser)';
const VALID_NIVEAU_ETUDES = [
  'Primaire', ...NIVEAUX_ANNEE_SECONDAIRE, 'Baccalauréat', 'Universitaire', NIVEAU_SECONDAIRE_ANCIEN,
];
const VALID_DIPLOME       = ['Bac', 'Licence', 'Master', 'Autre'];

// ══════════════════════════════════════════════════════════
//  NOUVELLE VALIDATION : niveau d'études / diplôme
// ══════════════════════════════════════════════════════════
export const validateNiveauEtudesDiplome = (niveauEtudes, diplomeObtenu) => {
  // Aucun niveau ou aucun diplôme : rien à vérifier (aucun champ n'est obligatoire)
  if (!niveauEtudes || !diplomeObtenu) return '';

  // Primaire et années du secondaire : pas encore de diplôme
  if (niveauEtudes === 'Primaire' || NIVEAUX_ANNEE_SECONDAIRE.includes(niveauEtudes)) {
    return `Pour le niveau ${niveauEtudes}, aucun diplôme ne peut être sélectionné.`;
  }

  // Baccalauréat (et l'ancien « Secondaire ») : seul le Bac a du sens
  if ((niveauEtudes === 'Baccalauréat' || niveauEtudes === NIVEAU_SECONDAIRE_ANCIEN)
      && diplomeObtenu !== 'Bac') {
    return `Pour le niveau ${niveauEtudes}, seul le diplôme "Bac" est accepté.`;
  }

  // Universitaire : tous les diplômes sont acceptés
  return '';
};

export const validateField = (field, value, allValues = {}) => {
  // ⚠️ CORRECTION : Gérer les différents types de valeurs (string, array, etc.)
  let v = value;
  
  // Si c'est une chaîne, on la trim
  if (typeof v === 'string') {
    v = v.trim();
  }
  
  // Si c'est un tableau, on vérifie sa longueur
  if (Array.isArray(v)) {
    v = v.length > 0 ? v : '';
  }

  switch (field) {

    case 'nom':
      if (!v)                   return '';
      if (v.length < 2)         return 'Le nom doit contenir au moins 2 caractères.';
      if (v.length > 50)        return 'Le nom ne peut pas dépasser 50 caractères.';
      if (!ALPHA_REGEX.test(v)) return 'Le nom ne doit contenir que des lettres, espaces ou tirets.';
      return '';

    case 'prenom':
      if (!v)                   return '';
      if (v.length < 2)         return 'Le prénom doit contenir au moins 2 caractères.';
      if (v.length > 50)        return 'Le prénom ne peut pas dépasser 50 caractères.';
      if (!ALPHA_REGEX.test(v)) return 'Le prénom ne doit contenir que des lettres, espaces ou tirets.';
      return '';

    case 'email':
      if (!v) return '';
      if (!EMAIL_REGEX.test(v)) return "Format d'email invalide (ex : nom@domaine.com).";
      if (v.length > 150)       return "L'email ne peut pas dépasser 150 caractères.";
      return '';

    case 'tel': {
      if (!v) return '';
      const cleaned = cleanPhoneNumber(v);
      if (!TEL_TN_REGEX.test(cleaned)) {
        return 'Numéro tunisien invalide. Format attendu : 8 chiffres commençant par 2, 4, 5, 7 ou 9 (ex: 55123456)';
      }
      return '';
    }

    case 'ville':
      if (!v)                   return '';
      if (!ALPHA_REGEX.test(v)) return 'La ville ne doit contenir que des lettres.';
      return '';

    case 'pays':
      if (v && !VALID_PAYS.includes(v)) return 'Veuillez sélectionner un pays valide.';
      return '';

    case 'dateNaissance': {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d.getTime())) return 'Date de naissance invalide.';
      const ageAns = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      if (ageAns < 6)   return "L'âge minimum est 6 ans.";
      if (ageAns > 100) return 'Date de naissance invalide.';
      return '';
    }

    case 'genre':
      if (v && !VALID_GENRE.includes(v)) return 'Veuillez sélectionner un genre valide.';
      return '';

    case 'niveauEtudes':
      if (v && !VALID_NIVEAU_ETUDES.includes(v)) return "Veuillez sélectionner un niveau d'études valide.";
      return '';

    case 'diplomeObtenu':
      // Validation seulement si un niveau est sélectionné
      if (allValues.niveauEtudes) {
        const crossError = validateNiveauEtudesDiplome(allValues.niveauEtudes, v);
        if (crossError) return crossError;
      }
      // Validation du format du diplôme si présent
      if (v && !VALID_DIPLOME.includes(v)) return 'Veuillez sélectionner un diplôme valide.';
      return '';

    case 'source':
      if (v && !VALID_SOURCE.includes(v)) return 'Veuillez sélectionner une source valide.';
      return '';

    // ⚠️ CORRECTION : Remplacer 'formation' par 'formations' (tableau)
    case 'formations':
      // Facultatif : la fiche peut être créée avant de connaître la formation
      return '';

    case 'niveau':
      if (v && !VALID_NIV.includes(v)) return 'Veuillez sélectionner un niveau valide.';
      return '';

    case 'modePreference':
      if (v && !VALID_MODE.includes(v)) return 'Veuillez sélectionner un mode valide.';
      return '';

    case 'statut':
      if (v && !VALID_STATUT.includes(v)) return 'Veuillez sélectionner un statut valide.';
      return '';

    case 'canalContact':
      return '';

    case 'commentaires':
      if (v && typeof v === 'string' && v.length > 500) return 'Les commentaires ne peuvent pas dépasser 500 caractères.';
      return '';

    default:
      return '';
  }
};

export const validateAll = (fd) => {
  // ⚠️ CORRECTION : Remplacer 'formation' par 'formations'
  const allFields = [
    'nom', 'prenom', 'email', 'tel', 'ville', 'pays',
    'dateNaissance', 'genre', 'niveauEtudes', 'diplomeObtenu',
    'source', 'formations', 'niveau', 'modePreference',  // ← 'formations' au lieu de 'formation'
    'statut',
    'commentaires',
  ];

  const errors = {};

  allFields.forEach((field) => {
    const msg = validateField(field, fd[field], fd);
    if (msg) errors[field] = msg;
  });

  // Validation croisée supplémentaire
  const crossError = validateNiveauEtudesDiplome(fd.niveauEtudes, fd.diplomeObtenu);
  if (crossError) errors.diplomeObtenu = crossError;

  // Seule exigence : pouvoir recontacter la personne (même règle que le serveur)
  if (!fd.nom?.trim() && !fd.tel?.trim()) {
    const msg = 'Renseignez au moins un nom ou un numéro de téléphone.';
    errors.nom = errors.nom || msg;
    errors.tel = errors.tel || msg;
  }

  return { errors, isValid: Object.keys(errors).length === 0 };
};